import {Injectable} from '@angular/core';
import {Company} from './company';
import {catchError, from, of, switchMap, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class LogoCache {

  private readonly LOCAL_KEY = "company_logo";
  private readonly DEFAULT_LOGO = "/logo.svg";

  constructor(private readonly companyService: Company, private http: HttpClient) {
  }

  getLogo() {
    const cached = localStorage.getItem(this.LOCAL_KEY);

    if (cached?.startsWith('data:image')) {
      return of(cached);
    }

    return this.fetchAndCacheLogo();
  }

  refreshLogo() {
    return this.fetchAndCacheLogo();
  }

  clear() {
    localStorage.removeItem(this.LOCAL_KEY);
  }

  private fetchAndCacheLogo() {
    return this.companyService.retrieveCompanyLogoUrl().pipe(switchMap(response => {
      if (!response || response === "logo.svg") {
        return of(this.DEFAULT_LOGO);
      }
      return this.retrieveLogo(response).pipe(catchError(() => of(this.DEFAULT_LOGO)));
    }));
  }

  private retrieveLogo(url: string) {
    return this.http.get(url, {responseType: "blob"}).pipe(
      switchMap(blob => {
        if (!blob.type.startsWith('image/')) {
          throw new Error(`Unexpected logo response type: ${blob.type}`);
        }
        return from(this.blobToBase64(blob));
      }),
      tap(base64 => localStorage.setItem(this.LOCAL_KEY, base64)),
    );
  }

  private blobToBase64(blob: Blob) {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    })
  }
}
